import deepmerge from 'deepmerge';
import busyGif from 'images/busy.gif';
import jQuery from 'jquery';
import { debounce } from 'lodash';
import Popper, { type PopperOptions } from 'popper.js';

type TriggerElement = HTMLElement | SVGElement;

export interface TooltipOptions {
  cache?: boolean;
  class?: string;
  container?: string;
  content?: string;
  contentHTML?: string;
  contentRef?: string;
  contentUrl?: string;
  delay?: {
    show: number;
    hide: number;
  };
  placement?: PopperOptions['placement'];
  popper?: PopperOptions;
  selector?: string;
  template?: string;
  title?: string;
  titleHTML?: string;
  titleRef?: string;
  trigger?: string;
  type?: TooltipType;
}

export type TooltipType = 'default' | 'tooltip' | 'popover';

export type TooltipAction =
  | 'enable'
  | 'disable'
  | 'hide'
  | 'destroy'
  | 'show'
  | 'toggle'
  | 'toggleEnabled'
  | 'dispose';

type OnOffEvents = Array<
  ['click'] | ['focus', 'blur'] | ['mouseenter', 'mouseout']
>;

const namespace = 'AhaTooltip';

// Someday when we no longer have to support IE11 we can just change this to 'template'.
const templateSelector = 'script[type="text/template"]';

// ---------- DEFAULT SETTINGS ----------
const defaultSettings: TooltipOptions = {
  cache: false,
  class: undefined,
  container: 'body',
  content: undefined,
  contentHTML: undefined,
  contentRef: undefined,
  contentUrl: undefined,
  delay: {
    show: 0,
    hide: 0,
  },
  placement: undefined,
  popper: {
    placement: 'auto',
    eventsEnabled: true,
    removeOnDestroy: false,
    modifiers: {
      arrow: {
        element: '.aha-tooltip__arrow',
      },
      offset: {
        offset: 0,
      },
      hide: {
        enabled: true,
      },
      preventOverflow: {
        enabled: true,
        boundariesElement: 'scrollParent',
      },
    },
  },
  selector: undefined,
  template: undefined,
  title: undefined,
  titleHTML: undefined,
  titleRef: undefined,
  trigger: 'hover focus',
  type: undefined,
};

let ahaTooltipTriggerObserver: MutationObserver;

// ---------- UTILITY FUNCTIONS ---------
function notUndefined(...args) {
  return args.find(x => typeof x !== 'undefined');
}

function findRef(trigger: TriggerElement, ref: string) {
  if (!trigger || !ref) return;
  return $(trigger).find(ref)[0] || $(document).find(ref)[0];
}

function getBodyData() {
  const bodyElem = document.querySelector('body');
  if (!bodyElem) return {};
  return $.data(bodyElem)[namespace] || ($.data(bodyElem)[namespace] = {});
}

// Callable function to begin watching the DOM for open tooltips.
const watchForOpenTooltips = () => {
  const $data = getBodyData();
  // If they <body> is already watching for open tooltips, do nothing.
  // Otherwise, begin watching for tooltips.
  if (!$data.watchForOpenTooltips) {
    $data.watchForOpenTooltips = true;
    const body = document.querySelector('body');
    if (!body) return;
    ahaTooltipTriggerObserver.observe(body, {
      childList: true,
      subtree: true,
    });
  }
};

// If the triggering element has been removed from the page, ensure the tooltip is removed too.
const removeOrphanTooltips = debounce((observer: MutationObserver) => {
  requestAnimationFrame(() => {
    const $openTooltips = $('.aha-tooltip');
    if ($openTooltips.length > 0) {
      $openTooltips.each((i, tooltip) => {
        const $tooltip = $(tooltip);
        const $trigger = $($tooltip.data('trigger'));
        if ($trigger.closest('body').length === 0) {
          $trigger.removeData();
          $tooltip.removeData();
          if (tooltip.parentElement) {
            tooltip.parentElement.removeChild(tooltip);
            // Removing the tooltip can cause its own mutation event to fire. Prevent this from happening.
            observer.takeRecords();
          }
        }
      });
    }
    // If all tooltips have been closed, then stop watching the <body> for mutations.
    if ($('.aha-tooltip').length === 0) {
      const $data = getBodyData();
      $data.watchForOpenTooltips = false;
      observer.disconnect();
    }
  });
}, 250);

// ---------- TOOLTIP FACTORY ----------
class AhaTooltip {
  type: TooltipType;
  triggerElem: TriggerElement;
  opts: TooltipOptions;
  enabled: boolean;
  isVisible: boolean;
  popper: Popper & { popper: HTMLElement };
  data: Record<string, any>;
  timeout: NodeJS.Timeout;
  triggerElemMutationObserver: MutationObserver;
  titleContentMutationObserver: MutationObserver;
  mutationObservers: Record<string, MutationObserver> = {};

  constructor(triggerElem: TriggerElement, opts: TooltipOptions) {
    this.type = opts.type ?? 'default';
    this.triggerElem = triggerElem;
    this.opts = opts;
    this.enabled = true;
    this.isVisible = false;
    this.createTooltip();
  }

  openTooltip(delayShow?: number) {
    const data = AhaTooltip.getData(this.triggerElem);

    // Close any other type of tooltip that is open for this triggering element.
    Object.keys(data).forEach(type => {
      if (type !== this.opts.type && type !== 'popper') {
        data[type].tooltip.closeTooltip(0);
      }
    });
    this.getContents();
    this.observeRefs();
    this.appendTooltip(delayShow);
  }

  observeRefs() {
    ['title', 'content'].forEach(refType => {
      const ref = findRef(this.triggerElem, this.opts[`${refType}Ref`]);

      if (ref) {
        if (this.mutationObservers[refType]) {
          this.mutationObservers[refType].disconnect();
          delete this.mutationObservers[refType];
        }

        const observer = new MutationObserver(this.refMutationCallback);
        this.mutationObservers[refType] = observer;
        observer.observe(ref, {
          characterData: true,
          subtree: true,
        });
      }
    });
  }

  // Observer titleRef/contentRef elements for changes, and update the tooltip contents if found.
  refMutationCallback: MutationCallback = () => {
    this.updateTooltipContent();
  };

  appendTooltip(delayShow?: number) {
    if (this.enabled) {
      clearTimeout(this.timeout);
      this.isVisible = true;
      this.timeout = setTimeout(
        () => {
          if (
            this.isVisible &&
            ((typeof this.opts.title === 'string' &&
              this.opts.title.trim().length > 0) ||
              this.opts.contentUrl ||
              (this.opts.content && this.opts.content.trim().length > 0))
          ) {
            // eslint-disable-line no-extra-parens
            this.repositionTooltip();
            let container: TriggerElement | null = null;
            if (this.opts.container === 'self') {
              container = this.triggerElem;
            } else if (this.opts.container) {
              container = document.querySelector<TriggerElement>(
                this.opts.container
              );
            }
            if (container) {
              $(container).append(this.popper.popper);
              this.popper.popper.showPopover?.();
              $(this.popper.popper).data({
                trigger: this.triggerElem,
                type: this.type,
              });
              requestAnimationFrame(() => {
                if (!this.popper.popper.hasAttribute('x-in')) {
                  this.popper.popper.setAttribute('x-in', '');
                  if (
                    this.triggerElem.hasAttribute(
                      'data-popover-no-pointer-events'
                    )
                  ) {
                    this.popper.popper.setAttribute('x-no-pointer-events', '');
                  }
                }
                watchForOpenTooltips();
              });
            }
          }
        },
        typeof delayShow === 'undefined' ? this.opts.delay?.show : delayShow
      );
    }
  }

  getTemplate() {
    const $template = this.opts.template
      ? $(this.opts.template)
      : $(`<div class="${[
          'aha-tooltip',
          `aha-tooltip--${this.type || 'default'}`,
          this.opts.class || '',
        ]
          .join(' ')
          .trim()}" role="tooltip" popover="manual">
      <div class="aha-tooltip__arrow"></div>
      <div class="aha-tooltip__inner">
        <div class="aha-tooltip__title" x-title></div>
        <div class="aha-tooltip__content" x-content></div>
      </div>
    </div>`);
    return $template[0];
  }

  createTooltip() {
    const template = this.getTemplate();

    if (this.opts.placement && this.opts.popper) {
      this.opts.popper.placement = this.opts.placement;
    }
    if (
      this.opts.container === 'body' &&
      this.opts.popper?.modifiers?.preventOverflow
    ) {
      this.opts.popper.modifiers.preventOverflow.boundariesElement = 'viewport';
    }

    // @ts-expect-error
    this.popper = new Popper(this.triggerElem, template, {
      ...this.opts.popper,
      positionFixed: true, // popover="manual" elements are positioned relative to the document
    });

    const data = (this.data = AhaTooltip.getData(this.triggerElem));
    data[this.type] = {
      tooltip: this,
      popper: this.popper,
    };

    this.setData(data);

    // Watch for changes to title, data-title, data-tooltip, data-content and update the tooltip contents accordingly.
    // This enables us to change the title/data-title/data-tooltip/data-content of the tooltip triggering element
    // and have those changes automatically reflected in the tooltip popup.
    this.titleContentMutationObserver = new MutationObserver(
      (mutations, observer) =>
        this.titleContentMutationCallback(
          data,
          this.opts.type ?? 'default',
          mutations,
          observer
        )
    );
    this.titleContentMutationObserver.observe(this.triggerElem, {
      attributes: true,
      attributeFilter: [
        'title',
        'data-title',
        'data-content',
        `data-${this.opts.type}-title`,
        `data-${this.opts.type}-content`,
      ],
    });

    this.triggerElemMutationObserver = new MutationObserver(
      (mutations, observer) =>
        this.triggerElemMutationCallback(mutations, observer)
    );
    if (this.triggerElem && this.triggerElem.parentNode) {
      this.triggerElemMutationObserver.observe(this.triggerElem.parentNode, {
        childList: true,
      });
    }
  }

  setPreventEventExcept(eventName: string) {
    const data = AhaTooltip.getData(this.triggerElem);
    Object.defineProperty(data, 'preventEventExcept', {
      value: eventName,
      enumerable: false,
      configurable: true,
    });
  }

  clearPreventEventExcept() {
    const data = AhaTooltip.getData(this.triggerElem);
    delete data.preventEventExcept;
  }

  getContents() {
    let renderFromAjax = false;
    new Promise((resolve, reject) => {
      if (this.opts.contentUrl) {
        if (!this.opts.content || this.opts.cache === false) {
          if (/^\//.test(this.opts.contentUrl)) {
            this.popper.popper.removeAttribute('x-loading-error');
            this.popper.popper.setAttribute('x-loading', '');
            this.updateTooltipContent();
            // we control busyGif so this is safe.
            const scopedContent =
              this.popper.popper.querySelector(':scope [x-content]');
            if (scopedContent) {
              // eslint-disable-next-line no-unsanitized/property
              scopedContent.innerHTML = `<img src="${busyGif}" alt='Loading…' class="my-5" />`;
            }
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            $.ajax({
              url: this.opts.contentUrl,
              success: data => {
                renderFromAjax = true;
                this.opts.content = data;
              },
              error: data => {
                this.popper.popper.setAttribute('x-loading-error', '');
              },
              complete: data => {
                this.popper.popper.removeAttribute('x-loading');
                resolve(void 0);
              },
            });
          } else {
            reject(new Error('URL must be relative.'));
          }
        } else {
          renderFromAjax = true;
        }
      } else {
        resolve(void 0);
      }
    })
      .then(() => {
        this.updateTooltipContent(renderFromAjax);
      })
      .catch(e => {
        console.error(e);
      });
  }

  // Watch for changes to title, data-title, data-tooltip, and update the tooltip contents accordingly.
  titleContentMutationCallback = (
    tooltip,
    type: TooltipType,
    mutations: MutationRecord[],
    observer: MutationObserver
  ) => {
    mutations.forEach(mutation => {
      const target = mutation.target;
      if (!(target instanceof HTMLElement)) return;
      if (!mutation.attributeName) return;

      // If title/content or data-title/data-content updates and there is already
      // a data-[type]-title/data-[type]-content set then do nothing, as a typed
      // title/content value is more specific than a generic title/content or
      // data-title/data-content.
      if (
        /^(data-)?title$/.test(mutation.attributeName) &&
        target.dataset[`${type}Title`]
      )
        return;
      if (
        /^(data-)?content$/.test(mutation.attributeName) &&
        target.dataset[`${type}Content`]
      )
        return;

      const key = /title/.test(mutation.attributeName) ? 'title' : 'content';
      const value = target.getAttribute(mutation.attributeName);

      if (mutation.attributeName === 'title' && value) {
        target.dataset.title = value;
        target.removeAttribute('title');
      }
      this.opts[key] = value ?? undefined;
      this.updateTooltipContent();
      observer.takeRecords();
    });
  };

  // Watch for triggerElem removals, when that happens destroy the tooltip and remove the observer.
  triggerElemMutationCallback = (
    mutations: MutationRecord[],
    observer: MutationObserver
  ) => {
    mutations.forEach(mutation => {
      if (Array.from(mutation.removedNodes).includes(this.triggerElem)) {
        this.destroyTooltip();
        observer.disconnect();
      }
    });
  };

  updateTooltipContent(renderFromAjax?: boolean) {
    ['title', 'content'].forEach(attr => {
      const elem = this.popper.popper.querySelector(`[x-${attr}]`);
      if (!elem) return;

      if (attr === 'content' && renderFromAjax) {
        elem.innerHTML = '';

        // tooltip contents come from children of this element, a fetch-html param,
        // or contentHTML property. in each case the consumer of the aha-tooltip API
        // is responsible for ensuring safety; always sanitizing may break intended scripting.
        if (this.opts[attr]) {
          // eslint-disable-next-line no-unsanitized/method
          const fragment = document
            .createRange()
            .createContextualFragment(this.opts[attr]);
          elem.appendChild(fragment);
        }
      } else if (this.opts[`${attr}HTML`]) {
        // tooltip contents come from children of this element, a fetch-html param,
        // or contentHTML property. in each case the consumer of the aha-tooltip API
        // is responsible for ensuring safety. always sanitizing may break intended scripting.
        // eslint-disable-next-line no-unsanitized/property
        elem.innerHTML = this.opts[attr] = this.opts[`${attr}HTML`];
      } else {
        const ref = this.opts[`${attr}Ref`];
        if (ref) {
          const refElem = findRef(this.triggerElem, ref);
          if (refElem) {
            // this appears to already be HTML in the DOM
            // eslint-disable-next-line no-unsanitized/property
            elem.innerHTML = this.opts[attr] = refElem.innerHTML;
          } else {
            Error(`Could not find ${attr}Ref element.`);
          }
        } else if (this.opts[attr]) {
          elem.textContent = this.opts[attr];
        }
      }
    });
    this.popper.scheduleUpdate();
  }

  repositionTooltip() {
    this.popper.scheduleUpdate();
  }

  fadeOut = () => {
    this.popper.popper.removeEventListener('transitionend', this.fadeOut);
    if (this.popper.popper.parentNode && !this.isVisible) {
      this.popper.popper.parentNode.removeChild(this.popper.popper);
    }
  };

  closeTooltip(delayHide?: number) {
    if (this.enabled) {
      if (this.popper) {
        clearTimeout(this.timeout);
        if (this.isVisible) {
          this.isVisible = false;
          this.timeout = setTimeout(
            () => {
              if (!this.isVisible) {
                this.popper.popper.addEventListener(
                  'transitionend',
                  this.fadeOut
                );
                this.popper.popper.removeAttribute('x-in');
              }
            },
            typeof delayHide === 'undefined' ? this.opts.delay?.hide : delayHide
          );
        } else {
          if (this.popper.popper.parentNode && !this.isVisible) {
            this.popper.popper.parentNode.removeChild(this.popper.popper);
          }
          this.popper.popper.removeAttribute('x-in');
        }
        for (const observer of Object.values(this.mutationObservers)) {
          observer.disconnect();
        }
        this.mutationObservers = {};
      }
    }
  }

  toggleTooltip() {
    if (this.isVisible) {
      this.closeTooltip();
    } else {
      this.openTooltip();
    }
  }

  enableTooltip() {
    this.enabled = true;
  }

  disableTooltip() {
    this.enabled = false;
  }

  toggleEnabled() {
    if (this.enabled) {
      this.disableTooltip();
    } else {
      this.enableTooltip();
    }
  }

  destroyTooltip() {
    this.closeTooltip(0);
    const data = AhaTooltip.getData(this.triggerElem);
    delete data[this.type];
  }

  static getOnOffEvents(opts: TooltipOptions): OnOffEvents {
    return (
      AhaTooltip.triggers(opts)?.reduce<OnOffEvents>((acc, trigger) => {
        if (trigger === 'click') {
          acc.push(['click']);
        } else if (trigger === 'focus') {
          acc.push(['focus', 'blur']);
        } else if (trigger === 'hover') {
          acc.push(['mouseenter', 'mouseout']);
        }
        return acc;
      }, []) ?? []
    );
  }

  static triggers(opts: TooltipOptions) {
    return opts.trigger?.split(' ').map(x => x.trim());
  }

  static capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  static parseValue(value: any) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  }

  static mergeInlineOpts(elem: TriggerElement, opts: TooltipOptions = {}) {
    const settings = Object.assign({}, defaultSettings);
    Object.keys(settings).forEach(key => {
      // Ensure that 'popper', 'contentHTML', and 'titleHTML' cannot be set from data-attributes.
      if (key === 'popper' || key === 'contentHTML' || key === 'titleHTML')
        return;
      let tempValue;
      let value;
      const typeKey = `${opts.type}${AhaTooltip.capitalizeFirstLetter(key)}`;
      if (key === 'title') {
        value =
          elem.dataset?.[typeKey] ||
          elem.dataset?.[key] ||
          (elem.dataset?.tooltip !== 'true' && elem.dataset?.tooltip) ||
          elem.getAttribute(key) ||
          opts[key]; // eslint-disable-line no-extra-parens

        // Remove elem.title attribute to prevent standard browser behavior of showing title attribute in native tooltip.
        if (elem instanceof HTMLElement && elem.title) {
          elem.dataset.title = elem.title;
          elem.removeAttribute('title');
        }
      } else if (typeof settings[key] === 'boolean') {
        try {
          tempValue = [
            elem.dataset?.[typeKey],
            elem.dataset?.[key],
            opts[key],
          ].find(x => typeof x !== 'undefined');
          value = JSON.parse(tempValue);
        } catch (e) {
          value = true;
        }
      } else {
        value = notUndefined(
          elem.dataset?.[typeKey],
          elem.dataset?.[key],
          opts[key]
        );

        if (key === 'delay') {
          // The delay value may appear as a strigified JSON object. Ensure that it can be parsed correctly.
          value =
            typeof value === 'string'
              ? value.replace(/'|&#22;|&quot;/g, '"')
              : value;
        } else if (/^(true)?$/.test(value)) {
          // If value is '' or true then we should assume that titleRef/contentRef will use the "default" placement of templates.
          // Title will be the first child templateSelector.
          // Content will be the last child templateSelector.
          if (/(t|T)itleRef$/.test(typeKey) || /(t|T)itleRef$/.test(key)) {
            value = `> ${templateSelector}:first`;
          } else if (
            /(c|C)ontentRef$/.test(typeKey) ||
            /(c|C)ontentRef$/.test(key)
          ) {
            value = `> ${templateSelector}:last`;
          }
        }
      }
      opts[key] = AhaTooltip.parseValue(value);
    });
    return opts;
  }

  static getData(elem: TriggerElement): Record<string, any> {
    return $.data(elem)[namespace] || {};
  }

  setData(data: any) {
    $.data(this.triggerElem)[namespace] = data;
  }

  static getTooltip(
    triggerElem: TriggerElement,
    type: TooltipType
  ): AhaTooltip | undefined {
    const data = AhaTooltip.getData(triggerElem);
    return data[type] && data[type].tooltip;
  }

  static getOrCreateTooltip(
    triggerElem: TriggerElement,
    opts: TooltipOptions
  ): AhaTooltip {
    opts = AhaTooltip.mergeInlineOpts(triggerElem, opts);
    return (
      AhaTooltip.getTooltip(triggerElem, opts.type ?? 'default') ||
      new AhaTooltip(triggerElem, opts)
    );
  }

  static bindEvents(this: JQuery, opts: TooltipOptions) {
    const selector = opts.selector;
    if (!selector) return;
    const events = AhaTooltip.getOnOffEvents(opts);
    events.forEach(event => {
      const [onEvent, offEvent] = event;
      if (offEvent) {
        $(this)
          .on(onEvent, opts.selector, e => {
            const tooltip = AhaTooltip.getOrCreateTooltip(
              e.currentTarget,
              Object.assign({}, opts)
            );
            if (
              !tooltip.data.preventEventExcept ||
              tooltip.data.preventEventExcept === onEvent
            ) {
              tooltip.openTooltip();
            }
          })
          .on(offEvent, selector, e => {
            if (
              !(
                e.currentTarget instanceof HTMLElement ||
                e.currentTarget instanceof SVGElement
              )
            ) {
              return;
            }

            const tooltip = AhaTooltip.getOrCreateTooltip(
              e.currentTarget,
              Object.assign({}, opts)
            );
            if (
              !tooltip.data.preventEventExcept ||
              tooltip.data.preventEventExcept === offEvent
            ) {
              const triggerElem = e.currentTarget;

              if (
                e.relatedTarget instanceof Node &&
                triggerElem.contains(e.relatedTarget)
              )
                return;
              if (
                offEvent === 'mouseout' &&
                e.relatedTarget === tooltip.popper.popper
              ) {
                $(tooltip.popper.popper).on(
                  'mouseout',
                  function mouseLeaveHandler(mouseoutEvent) {
                    if (
                      !tooltip.popper.popper.contains(
                        mouseoutEvent.relatedTarget as Node
                      ) &&
                      !triggerElem.contains(mouseoutEvent.relatedTarget as Node)
                    ) {
                      tooltip.closeTooltip();
                      $(tooltip.popper.popper).off(
                        'mouseleave',
                        mouseLeaveHandler
                      );
                    }
                  }
                );
              } else {
                tooltip.closeTooltip();
              }
            }
          });
      } else {
        $(this).on(onEvent, opts.selector, e => {
          const triggerElem = e.currentTarget;
          const tooltip = AhaTooltip.getOrCreateTooltip(
            triggerElem,
            Object.assign({}, opts)
          );
          if (
            !tooltip.data.preventEventExcept ||
            tooltip.data.preventEventExcept === onEvent
          ) {
            if (tooltip.isVisible) {
              tooltip.closeTooltip();
              tooltip.clearPreventEventExcept();
            } else {
              tooltip.openTooltip();
              tooltip.setPreventEventExcept(onEvent);
              requestAnimationFrame(() => {
                $(document).on(
                  onEvent,
                  function handleClickOutside(clickOutsideEvent) {
                    const selectroModal =
                      document.querySelector('#selectro-modal');
                    const datePicker = document.querySelector('.datepicker');
                    // Jquery typing forces this to be Document but it will actually be any HTMLElement
                    const target =
                      clickOutsideEvent.target as unknown as HTMLElement;
                    // Sometimes we render bookmark filters in an aha-tooltip. Date filters open up
                    // an aha-menu-content and clicking in the menu closes
                    // the tooltip and thus the filter. This prevents the tooltip from closing
                    const ahaMenuContent = target.closest(
                      'aha-menu-content[open]'
                    );

                    if (triggerElem === target) {
                      $(document).off(onEvent, handleClickOutside as any);
                    } else {
                      if (
                        !tooltip.popper.popper.contains(target) &&
                        (!selectroModal || !selectroModal.contains(target)) &&
                        (!datePicker || !datePicker.contains(target)) &&
                        !ahaMenuContent &&
                        // these are required because the parent (.datepicker) is destroyed by the time we get the event
                        !target.classList.contains('day') &&
                        !target.classList.contains('month') &&
                        !target.classList.contains('year')
                      ) {
                        $(document).off(onEvent, handleClickOutside as any);
                        tooltip.closeTooltip();
                        tooltip.clearPreventEventExcept();
                      }
                    }
                  }
                );
              });
            }
          }
        });
      }
    });
  }

  static createManualTooltips(this: JQuery, opts: TooltipOptions) {
    $(this).each((i, elem) => {
      AhaTooltip.getOrCreateTooltip(elem, opts);
    });
  }

  static manualTypeMutationCallback = (
    selector: string,
    opts: TooltipOptions,
    mutations
  ) => {
    $(document)
      .find(selector)
      .each((i, elem) => {
        AhaTooltip.getOrCreateTooltip(elem, opts);
      });
  };

  static setup(
    this: JQuery,
    opts: TooltipOptions | TooltipAction,
    type: TooltipType
  ) {
    if (typeof opts !== 'string') {
      opts.type = type;
      const settings = Object.assign({}, defaultSettings);
      const mergedOpts = deepmerge(settings, opts);

      // This type of tooltip will only ever be controlled by manual show/hide controls.
      if (mergedOpts.trigger === 'manual') {
        if (mergedOpts.selector) {
          // If a selector is specified, watch for any additional triggers added to the page.
          const observer = new MutationObserver(mutations =>
            AhaTooltip.manualTypeMutationCallback(
              mergedOpts.selector,
              mergedOpts,
              mutations
            )
          );
          const body = document.querySelector('body');
          if (!body) return;
          observer.observe(body, {
            childList: true,
            subtree: true,
          });

          AhaTooltip.createManualTooltips.call(
            $(mergedOpts.selector),
            mergedOpts
          );
        } else {
          AhaTooltip.createManualTooltips.call(this, mergedOpts);
        }
      } else {
        AhaTooltip.bindEvents.call(this, mergedOpts);
      }
    } else {
      const settings = Object.assign({}, defaultSettings, { type });
      switch (opts) {
        case 'show':
          this.each((i, elem) => {
            const tooltip = AhaTooltip.getOrCreateTooltip(elem, settings);
            if (tooltip) tooltip.openTooltip(0);
          });
          break;
        case 'hide':
          this.each((i, elem) => {
            const tooltip = AhaTooltip.getOrCreateTooltip(elem, settings);
            if (tooltip) tooltip.closeTooltip(0);
          });
          break;
        case 'toggle':
          this.each((i, elem) => {
            const tooltip = AhaTooltip.getOrCreateTooltip(elem, settings);
            if (tooltip) tooltip.toggleTooltip();
          });
          break;
        case 'enable':
          this.each((i, elem) => {
            const tooltip = AhaTooltip.getOrCreateTooltip(elem, settings);
            if (tooltip) tooltip.enableTooltip();
          });
          break;
        case 'disable':
          this.each((i, elem) => {
            const tooltip = AhaTooltip.getOrCreateTooltip(elem, settings);
            if (tooltip) tooltip.disableTooltip();
          });
          break;
        case 'toggleEnabled':
          this.each((i, elem) => {
            const tooltip = AhaTooltip.getOrCreateTooltip(elem, settings);
            if (tooltip) tooltip.toggleEnabled();
          });
          break;
        case 'dispose':
        case 'destroy':
          this.each((i, elem) => {
            const tooltip = AhaTooltip.getTooltip(elem, type);
            if (tooltip) tooltip.destroyTooltip();
          });
          break;
        default:
      }
    }
  }
}

(function ($) {
  // Close tooltip if user clicks on a non-ajax <a> within that tooltip.
  $(document).on('click', '.aha-tooltip a', e => {
    const { trigger, type } = $(e.currentTarget).closest('.aha-tooltip').data();
    $(trigger)[type]('hide');
  });

  // If the trigger for a tooltip is removed from the DOM, remove the tooltip too.
  ahaTooltipTriggerObserver = new MutationObserver((mutations, observer) => {
    if (mutations.some(mutation => mutation.removedNodes.length > 0)) {
      removeOrphanTooltips(observer);
    }
  });

  // @ts-expect-error we're overriding jquery ui
  $.fn.tooltip = function (opts: TooltipAction | TooltipOptions = {}): JQuery {
    AhaTooltip.setup.call(this, opts, 'tooltip');
    return this;
  };

  $.fn.popover = function (opts: TooltipAction | TooltipOptions = {}): JQuery {
    AhaTooltip.setup.call(this, opts, 'popover');
    return this;
  };
})(jQuery);
